import {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useImperativeHandle,
} from 'react';
import {
  FormProvider as ReactHookFormProvider,
  Path,
  useForm,
  useFormContext,
  UseFormReturn,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { Schema } from 'yup';

import Flex, {
  FlexComponentProps,
} from '@decisiv/ui-components/lib/components/Flex';

import DeprecatedForm from './deprecatedForm';

interface FormProviderProps<T extends Record<string, any>> {
  initialValues: T | Partial<T> | undefined;
  validationSchema?: Schema<T>;
  formConfig?: any;
  children: ReactNode;
}

export interface FormHandler<T extends Record<string, any>> {
  reset: () => void;
  setError: UseFormSetError<T>;
  setValue: UseFormSetValue<T>;
  submit: () => Promise<void>;
  setValues: (values: T) => void;
}

interface FormProps<T>
  extends FlexComponentProps,
    Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  onSubmit: (values: T) => void;
}

export const createForm = <T extends Record<string, any>>() => {
  const FormContext = createContext<UseFormReturn<T> | null>(null);

  const FormProvider = ({
    initialValues,
    validationSchema,
    formConfig,
    children,
  }: FormProviderProps<T>) => {
    const methods = useForm<T>({
      defaultValues: initialValues,
      /* istanbul ignore else -- @preserve */
      resolver: validationSchema ? yupResolver(validationSchema) : undefined,
      mode: 'onChange',
      reValidateMode: 'onChange',
      ...formConfig,
    });

    return (
      <ReactHookFormProvider {...methods}>
        <FormContext.Provider value={methods}>{children}</FormContext.Provider>
      </ReactHookFormProvider>
    );
  };

  FormProvider.propTypes = {
    initialValues: PropTypes.shape({}).isRequired,
    validationSchema: PropTypes.shape({}),
    resolver: PropTypes.func,
    children: PropTypes.node.isRequired,
    formConfig: PropTypes.shape({}),
  };

  FormProvider.defaultProps = {
    validationSchema: undefined,
    resolver: undefined,
    formConfig: {},
  };

  const Form = forwardRef<FormHandler<T>, FormProps<T>>(
    ({ onSubmit, children, ...props }, ref) => {
      const methods = useFormContext<T>();

      const {
        setValue,
        setError,
        reset,
        handleSubmit,
        watch,
        clearErrors,
        formState,
      } = methods;

      const handleOnSubmit = handleSubmit(onSubmit);

      /* istanbul ignore next -- @preserve */
      const setValues = useCallback(
        (values: Partial<T>) => {
          Object.entries(values).forEach(([key, value]) => {
            setValue(key as Path<T>, value);
          });
        },
        [setValue],
      );

      useImperativeHandle(
        ref,
        () => ({
          setValue,
          setValues,
          setError,
          reset,
          submit: handleOnSubmit,
          watch,
          clearErrors,
          formState,
        }),
        [
          setValue,
          setValues,
          setError,
          reset,
          handleOnSubmit,
          watch,
          clearErrors,
          formState,
        ],
      );

      return (
        <Flex as="form" onSubmit={handleOnSubmit} {...props}>
          {children}
        </Flex>
      );
    },
  );

  Form.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
  };

  Form.displayName = 'Form';

  return { FormProvider, Form, FormContext } as const;
};

export default DeprecatedForm;
export { FormProvider } from './deprecatedForm';

export { default as TextField } from './TextField';
export { default as PhoneTextField } from './PhoneTextField';
export { default as TextArea } from './TextArea';
export { default as TagInput } from './TagInput';
export { default as Combobox } from './Combobox';
export { default as Select } from './Select';
export { default as CheckboxList } from './CheckboxList';
export { default as SelectDate } from './SelectDate';
export { default as SelectDateRange } from './SelectDateRange';
export { default as RadioGroup } from './RadioGroup';
export { default as Checkbox } from './Checkbox';
export { default as CheckboxGroup } from './CheckboxGroup';
export { default as Toggle } from './Toggle';
export { default as SearchInput } from './SearchInput';
export { default as OptionsList } from './OptionsList';
export { default as FileInput } from './HiddenFileInput';
export { default as MultiTagCombobox } from './MultiTagCombobox';

export function useFieldWatch(fieldNames: any) {
  const { control } = useFormContext();
  return useWatch({
    control,
    name: fieldNames,
  });
}
